import html2canvas from 'html2canvas';
import { CanvasObject, TextObject, ShapeObject, ImageObject, ConnectorObject } from './types';
import { generateId } from './constants';

export const getCanvasCoordinates = (
    e: React.MouseEvent,
    canvasRef: React.RefObject<HTMLDivElement>,
) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = rect.width / canvasRef.current.offsetWidth;
    const scaleY = rect.height / canvasRef.current.offsetHeight;
    return {
        x: (e.clientX - rect.left) / scaleX,
        y: (e.clientY - rect.top) / scaleY,
    };
};

export const createObject = (type: string, x: number, y: number): CanvasObject => { 
    const id = generateId();
    const commonProps = {
        id,
        position: { x, y },
        size: { width: 200, height: 100 },
        color: 0xFF000000, // Black
        opacity: 1.0,
        zIndex: 0,
        rotation: 0,
    };

    switch (type) {
        case 'text':
            return {
                ...commonProps,
                type: 'text',
                size: { width: 200, height: 50 },
                text: 'Text',
                fontSize: 24,
                fontFamily: 'Roboto',
                fontWeight: 3,
                fontStyle: 0,
                textAlign: 0,
                isUnderlined: false,
                isItalic: false,
                isBold: false,
                borderWidth: 0,
                borderColor: 0xFF000000,
            } as TextObject;

        case 'rectangle':
            return {
                ...commonProps,
                type: 'rectangle',
                color: 0xFF3B82F6, // Modern nice blue (Tailwind Blue 500)
                size: { width: 200, height: 120 },
                borderWidth: 2,
                borderColor: 0xFF000000,
            } as ShapeObject;

        case 'circle':
            return {
                ...commonProps,
                type: 'circle',
                color: 0xFF22C55E, // Modern nice green (Tailwind Green 500)
                size: { width: 150, height: 150 },
                borderWidth: 2,
                borderColor: 0xFF000000,
            } as ShapeObject;
        case 'arrow':
        case 'connector':
            return {
                ...commonProps,
                type: 'arrow',
                size: { width: 200, height: 100 },
                endPosition: { x: x + 200, y: y },
                strokeWidth: 2,
                lineStyle: 'solid',
                arrowStyle: 'straight',
            } as ConnectorObject;

        case 'image':
            return {
                ...commonProps,
                type: 'image',
                size: { width: 250, height: 250 },
                imageUrl: '',
                imagePath: '',
                imageName: 'New Image',
                borderWidth: 0,
                borderColor: 0xFF000000,
            } as ImageObject;

        default:
            console.warn(`Unrecognized object type: "${type}", defaulting to rectangle`);
            return {
                ...commonProps,
                type: 'rectangle',
                size: { width: 200, height: 120 },
                borderWidth: 2,
                borderColor: 0xFF000000,
            } as ShapeObject;
    }
};

export const intToHex = (int: number = 0xFF000000): string => {
    try {
        const unsigned = int >>> 0;
        if (unsigned === 0) return 'transparent';
        const r = (unsigned >> 16) & 0xFF;
        const g = (unsigned >> 8) & 0xFF;
        const b = unsigned & 0xFF;
        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    } catch (e) {
        return '#000000';
    }
};

export const hexToInt = (hex: string): number => {
    try {
        if (!hex) return 0xFF000000;
        if (hex === 'transparent' || hex === 'rgba(0,0,0,0)') return 0x00000000;
        const cleanHex = hex.replace('#', '');

        if (cleanHex.length === 6) {
            const r = parseInt(cleanHex.substring(0, 2), 16);
            const g = parseInt(cleanHex.substring(2, 4), 16);
            const b = parseInt(cleanHex.substring(4, 6), 16);
            return ((0xFF << 24) | (r << 16) | (g << 8) | b) >>> 0;
        }
        return 0xFF000000;
    } catch (e) {
        return 0xFF000000;
    }
};

export const captureCanvas = async (canvasElement: HTMLElement): Promise<Blob | null> => {
    try {
        // Get the actual size of the element to ensure we capture the whole thing
        const rect = canvasElement.getBoundingClientRect();
        
        const canvas = await html2canvas(canvasElement, {
            useCORS: true,
            backgroundColor: '#ffffff',
            scale: 2,
            logging: true,
            width: rect.width,
            height: rect.height,
            scrollX: -window.scrollX,
            scrollY: -window.scrollY,
            onclone: (clonedDoc) => {
                const svgs = clonedDoc.querySelectorAll('svg');
                svgs.forEach(svg => {
                    svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
                    // Help html2canvas find the elements
                    svg.style.display = 'block';
                    svg.style.visibility = 'visible';
                    svg.style.opacity = '1';
                    svg.style.pointerEvents = 'auto';
                });
            }
        });

        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                resolve(blob);
            }, 'image/png');
        });
    } catch (e) {
        console.error('Failed to capture canvas:', e);
        return null;
    }
};